"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.noPatientsText = exports.cellText = exports.hasPracticeRows = void 0;
exports.measuresInOrder = measuresInOrder;
exports.byPerformance = byPerformance;
exports.passesFilter = passesFilter;
exports.matrixRows = matrixRows;
exports.unionMeasures = unionMeasures;
exports.newestTimes = newestTimes;
exports.practiceText = practiceText;
exports.groupRows = groupRows;
exports.practiceTable = practiceTable;
exports.groupTable = groupTable;
exports.groupPracticeKeys = groupPracticeKeys;
const format_js_1 = require("./format.js");
/** The measures in `tabs` order (CR2-060's configured order); any the tabs do not name come after. */
function measuresInOrder(tabs, measures) {
    const at = (m) => tabs.includes(m.measureNumber) ? tabs.indexOf(m.measureNumber) : tabs.length;
    return [...measures].sort((a, b) => at(a) - at(b));
}
/** CR2-033: by performance, NULL last in both directions, ties to the larger denominator. */
function byPerformance(sort) {
    const sign = sort.direction === "ascending" ? 1 : -1;
    return (a, b) => {
        const fa = a.figures.get(sort.measureKey);
        const fb = b.figures.get(sort.measureKey);
        const ra = fa?.performanceRate ?? null;
        const rb = fb?.performanceRate ?? null;
        if (ra === null || rb === null)
            return ra === rb ? 0 : ra === null ? 1 : -1;
        return sign * (ra - rb) || (fb?.denominator ?? 0) - (fa?.denominator ?? 0);
    };
}
/** The filter bar's test: the measure's rate (or any measure's) below or above the value. */
function passesFilter(row, filter) {
    const test = (f) => f?.performanceRate != null &&
        (filter.condition === "below"
            ? f.performanceRate < filter.value
            : f.performanceRate > filter.value);
    return filter.measureKey === null
        ? [...row.figures.values()].some(test)
        : test(row.figures.get(filter.measureKey));
}
function matrixRows(matrices, sort, filter) {
    return matrices.flatMap((m) => {
        const rows = m.rows.map((r) => ({
            key: `${m.acoCode}/${r.tin ?? "TOTAL"}`,
            acoCode: m.acoCode,
            tin: r.tin,
            name: r.name,
            configured: r.configured,
            isTotal: r.isTotal,
            figures: new Map(r.figures.map((f) => [f.measureKey, f])),
        }));
        let tins = rows.filter((r) => !r.isTotal);
        if (filter !== undefined)
            tins = tins.filter((r) => passesFilter(r, filter));
        if (sort !== undefined)
            tins = [...tins].sort(byPerformance(sort));
        return [...tins, ...rows.filter((r) => r.isTotal)];
    });
}
/**
 * J-209 (inbox 06 PR D item 2): the measures of several ACOs' matrices, each once, so an ACO without a
 * measure (or without data) does not blank another's columns. The first matrix naming a measure wins.
 */
function unionMeasures(matrices) {
    const byKey = new Map();
    for (const m of matrices)
        for (const measure of m.measures)
            if (!byKey.has(measure.measureKey))
                byKey.set(measure.measureKey, measure);
    return [...byKey.values()];
}
/** J-209 item 2: the newest load and calculation time across the matrices, null where none has one. */
function newestTimes(matrices) {
    const newest = (values) => values.reduce((max, v) => (v !== null && (max === null || v > max) ? v : max), null);
    return {
        dataLoadedAt: newest(matrices.map((m) => m.dataLoadedAt)),
        calculatedAt: newest(matrices.map((m) => m.calculatedAt)),
    };
}
/** J-209 item 14: whether any practice row is left (the TOTAL rows always stay). */
const hasPracticeRows = (rows) => rows.some((r) => !r.isTotal);
exports.hasPracticeRows = hasPracticeRows;
const cellText = (f) => f === undefined
    ? "—"
    : (0, format_js_1.formatPerformanceWithCounts)(f.performanceRate, f.numerator, f.denominator);
exports.cellText = cellText;
/** The practice cell as text: the name, else the TIN, and the TIN; the TOTAL row's configured label. */
function practiceText(row, totalLabel) {
    if (row.isTotal)
        return totalLabel.replace("{aco}", row.acoCode);
    return `${row.name ?? row.tin} (${row.tin})${row.configured ? "" : " — not configured"}`;
}
const counted = (f) => f.met + f.notMet + f.excluded + f.exceptions > 0;
function groupRows(views, sort, filter) {
    const shown = new Set(views.map((v) => v.acoCode));
    const placed = new Set();
    return views.flatMap((v) => {
        const rows = v.groups.flatMap((g) => {
            const spans = g.acoCodes.length > 1;
            const whole = spans && g.combined !== null && g.acoCodes.every((c) => shown.has(c));
            if (whole && placed.has(g.groupId))
                return [];
            if (whole)
                placed.add(g.groupId);
            const own = whole && g.combined !== null ? g.combined : g;
            return [
                {
                    key: `${v.acoCode}/${g.groupId}`,
                    acoCode: v.acoCode,
                    acoCodes: whole ? g.acoCodes : [v.acoCode],
                    figuresAco: spans && !whole ? v.acoCode : null,
                    groupId: g.groupId,
                    groupName: g.groupName,
                    isAcoWide: g.isAcoWide,
                    figures: new Map(own.figures.map((f) => [f.measureKey, f])),
                    hasPatients: own.figures.some(counted),
                },
            ];
        });
        let groups = rows.filter((r) => !r.isAcoWide);
        if (filter !== undefined)
            groups = groups.filter((r) => passesFilter(r, filter));
        if (sort !== undefined)
            groups = [...groups].sort(byPerformance(sort));
        return [...rows.filter((r) => r.isAcoWide), ...groups];
    });
}
/** The text a group without patients shows in place of its figures. */
const noPatientsText = (year) => `No patients in ${year}`;
exports.noPatientsText = noPatientsText;
/** The TIN Name view: its measure columns and its rows, as the screen and the workbook show them. */
function practiceTable(matrices, choices) {
    const measures = choices.measuresVisible
        ? measuresInOrder(choices.tabs, unionMeasures(matrices)).filter((m) => !choices.hiddenMeasures.includes(m.measureNumber))
        : [];
    const keys = choices.groupKeys;
    // J-277 (item 8b): with a group chosen, only its practices, and no ACO TOTAL row (not the group's).
    const rows = matrixRows(matrices, choices.sort, choices.filter).filter((r) => keys === undefined || (!r.isTotal && keys.has(`${r.acoCode}/${r.tin}`)));
    return { measures, rows };
}
/** The Performance group view: its measure columns and its rows, as the screen and the workbook show them. */
function groupTable(views, choices) {
    const granted = choices.grantedGroupIds;
    const shown = granted === undefined
        ? views
        : views.map((v) => ({ ...v, groups: v.groups.filter((g) => granted.includes(g.groupId)) }));
    const measures = measuresInOrder(choices.tabs, shown
        .flatMap((v) => v.measures)
        .filter((m, i, all) => all.findIndex((x) => x.measureKey === m.measureKey) === i)).filter((m) => !choices.hiddenMeasures.includes(m.measureNumber));
    return { measures, rows: groupRows(shown, choices.sort, choices.filter) };
}
/**
 * J-277 (item 8b): a chosen group's practices the TIN Name view keeps, as `<ACO>/<TIN>` keys: under the
 * header's ACO when one is chosen (`headerAco`), else in every ACO.
 */
function groupPracticeKeys(practices, headerAco) {
    return new Set(practices
        .filter((p) => headerAco === undefined || p.acoCode === headerAco)
        .map((p) => `${p.acoCode}/${p.tin}`));
}
//# sourceMappingURL=performance-rows.js.map